import type { Traveler } from "@b2bportal/traveler-api/lib/api";
import { getObjectKeysAsObject, setContextWithKey } from "@checkout/helpers";
import { ParentState, type TravelerError } from "@checkout/index";
import dayjs from "dayjs";
import { assign, type DoneInvokeEvent, sendParent } from "xstate";
import type { PartialParentWithTravelerContext } from "./context";
import {
  type SelectTravelerEvent,
  type SetCurrentTravelerEvent,
  type SetCurrentInfantIdEvent,
  type OnInfantModalContinueEvent,
  TravelerEventTypes,
} from "./events";

export const actions = {
  selectTraveler: assign(
    (ctx: PartialParentWithTravelerContext, event: SelectTravelerEvent) => {
      const travelerInformationCtx = ctx[ParentState.travelerInformation];
      const { travelerId, singleTravelerWorkflow, onUpdate = false } = event;
      const { selectedTravelerIds, travelers } = travelerInformationCtx;
      const newTravelerInfoCtx = { ...travelerInformationCtx };

      // Unselect the traveler if already exists in the selected list and action is not triggered by update
      if (!onUpdate && selectedTravelerIds.includes(travelerId)) {
        newTravelerInfoCtx.selectedTravelerIds = selectedTravelerIds.filter(
          (id) => id !== travelerId
        );
      } else {
        const newTraveler = travelers.find(
          (traveler) => traveler.id === travelerId
        );
        if (!newTraveler) return ctx;

        newTravelerInfoCtx.selectedTravelerIds = singleTravelerWorkflow
          ? [travelerId] // hotel and car have only one primary traveler
          : [...selectedTravelerIds, travelerId]; // air can have multiple travelers
      }

      return {
        ...ctx,
        [ParentState.travelerInformation]: newTravelerInfoCtx,
      };
    }
  ),

  selectFlightTraveler: assign(
    (ctx: PartialParentWithTravelerContext, event: SelectTravelerEvent) => {
      // We don't care about onUpdate, which is taken cared by separate action
      const { travelerId, singleTravelerWorkflow } = event;

      // For flights, we assume singleTravelerWorkflow is always false
      // If it's true, do nothing and return current context
      if (singleTravelerWorkflow) {
        return ctx;
      }

      const travelerInformationCtx = ctx[ParentState.travelerInformation];
      const { selectedTravelerIds, travelers } = travelerInformationCtx;

      // Get flight-specific context for INS checking
      // TODO: should traveler state depends on flight context or should be passed in from events?
      const departureDate = (ctx as any).flightSearch?.departureDate;
      const selectedFareId =
        (ctx as any).flightShop.selectedTrip.returnFareId ||
        (ctx as any).flightShop.selectedTrip.outgoingFareId;
      const fareDetails = (ctx as any).flightShop.tripDetails.fareDetails;
      const excludeINS = fareDetails
        ?.filter((fare) => fare.id === selectedFareId)
        ?.some((fare) => !!fare.excludeINS);

      const selectedLapInfantIds =
        travelerInformationCtx.selectedLapInfantIds || [];
      const newTravelerInfoCtx = { ...travelerInformationCtx };

      if (
        selectedTravelerIds.includes(travelerId) ||
        selectedLapInfantIds.includes(travelerId)
      ) {
        // TODO: onUpdate is not used yet, double check the logic when use it
        // If traveler is already selected, remove it to unselect it
        newTravelerInfoCtx.selectedTravelerIds = selectedTravelerIds.filter(
          (id) => travelerId !== id
        );
        if (selectedLapInfantIds.includes(travelerId)) {
          newTravelerInfoCtx.selectedLapInfantIds = selectedLapInfantIds.filter(
            (id) => travelerId !== id
          );
        }
      } else {
        // If traveler not selected, add to list to select it
        const newTraveler = travelers.find(
          (traveler) => traveler.id === travelerId
        );
        if (!newTraveler) {
          return setContextWithKey(
            ctx,
            ParentState.travelerInformation,
            newTravelerInfoCtx
          );
        }

        // Check if the traveler is an infant (under 2 years old)
        const referenceDate = departureDate ? dayjs(departureDate) : dayjs();
        const isNewTravelerInfant = newTraveler.dateOfBirth
          ? referenceDate.diff(dayjs(newTraveler.dateOfBirth), "year") < 2
          : false;

        if (isNewTravelerInfant) {
          // Handle adding infants - show error if no other travelers selected
          if (selectedTravelerIds.length === 0) {
            // Show Infant only error
            newTravelerInfoCtx.infantOnlyError = true;
          }

          if (excludeINS) {
            // If the excludeINS is true, we only allow infants to be added as an INF
            newTravelerInfoCtx.selectedLapInfantIds = [
              ...selectedLapInfantIds,
              travelerId,
            ];
          } else {
            // Set current infant ID for seat selection modal
            newTravelerInfoCtx.currentInfantId = newTraveler.id;
          }
        } else {
          // Add regular travelers (i.e. Not infants)
          newTravelerInfoCtx.selectedTravelerIds = [
            ...selectedTravelerIds.filter((id) => id !== travelerId),
            travelerId,
          ];
        }
      }

      return setContextWithKey(
        ctx,
        ParentState.travelerInformation,
        newTravelerInfoCtx
      );
    }
  ),

  updateTraveler: assign(
    (
      ctx: PartialParentWithTravelerContext,
      event: DoneInvokeEvent<Traveler>
    ) => {
      const newTraveler = event.data;
      const { travelers } = ctx[ParentState.travelerInformation];
      const newTravelers = travelers.some(
        (traveler) => traveler.id === newTraveler.id
      )
        ? travelers.map((traveler) =>
            traveler.id === newTraveler.id ? newTraveler : traveler
          )
        : [...travelers, newTraveler];

      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.travelers`,
        newTravelers
      );
      // Don't couple traveler update with select traveler action
      return ctx;
    }
  ),

  setTravelers: assign(
    (
      ctx: PartialParentWithTravelerContext,
      event: DoneInvokeEvent<Array<Traveler>>
    ) => {
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.travelers`,
        event.data
      );
      return ctx;
    }
  ),

  goPrevious: sendParent(TravelerEventTypes.PREVIOUS),

  handleDeleteTraveler: assign(
    (ctx: PartialParentWithTravelerContext, event: DoneInvokeEvent<string>) => {
      const deletedTravelerId = event.data;
      const selectedTravelerIds =
        ctx[ParentState.travelerInformation].selectedTravelerIds;
      ctx[ParentState.travelerInformation].selectedTravelerIds =
        selectedTravelerIds.filter((id) => deletedTravelerId !== id);

      const travelers = ctx[ParentState.travelerInformation].travelers;
      ctx[ParentState.travelerInformation].travelers = travelers.filter(
        (traveler) => traveler.id !== deletedTravelerId
      );
      return ctx;
    }
  ),

  setCurrentTraveler: assign(
    (ctx: PartialParentWithTravelerContext, event: SetCurrentTravelerEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.currentTraveler`,
        event.traveler
      )
  ),

  setCurrentInfantId: assign(
    (ctx: PartialParentWithTravelerContext, event: SetCurrentInfantIdEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.currentInfantId`,
        event.infantId
      )
  ),

  onInfantModalContinue: assign(
    (
      ctx: PartialParentWithTravelerContext,
      { seatType }: OnInfantModalContinueEvent
    ) => {
      const travelerInformationCtx = ctx[ParentState.travelerInformation];

      // Get current infant ID and existing arrays
      const currentInfantId = travelerInformationCtx.currentInfantId;
      let selectedLapInfantIds =
        travelerInformationCtx.selectedLapInfantIds || [];
      let selectedTravelerIds = travelerInformationCtx.selectedTravelerIds;

      if (seatType === "OwnSeat") {
        // Remove from lap infants and add to regular travelers
        selectedLapInfantIds = selectedLapInfantIds.filter(
          (id: string) => id !== currentInfantId
        );

        selectedTravelerIds = [
          ...selectedTravelerIds,
          currentInfantId as string,
        ];
      } else if (seatType === "OnLap") {
        // Add to lap infants
        selectedLapInfantIds = [
          ...selectedLapInfantIds,
          currentInfantId as string,
        ];
      } else if (seatType === "NotTraveling") {
        // Remove from all selected IDs (infant is not traveling)
        selectedLapInfantIds = selectedLapInfantIds.filter(
          (id: string) => id !== currentInfantId
        );

        selectedTravelerIds = selectedTravelerIds.filter(
          (id: string) => id !== currentInfantId
        );
      }

      // Update the context
      const updatedContext = {
        ...travelerInformationCtx,
        selectedTravelerIds,
        selectedLapInfantIds,
      };

      return setContextWithKey(
        ctx,
        ParentState.travelerInformation,
        updatedContext
      );
    }
  ),
  clearCurrentInfantId: assign(
    (ctx: PartialParentWithTravelerContext, _event) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.currentInfantId`,
        undefined
      )
  ),

  setTravelersError: assign(
    (
      ctx: PartialParentWithTravelerContext,
      event: DoneInvokeEvent<TravelerError>
    ) =>
      setContextWithKey(
        ctx,
        `${ParentState.travelerInformation}.error`,
        event.data
      )
  ),

  clearTravelerInformationError: assign(
    (ctx: PartialParentWithTravelerContext, _event) => {
      ctx[ParentState.travelerInformation].error = undefined;
      return ctx;
    }
  ),

  unselectAllTravelers: assign(
    (ctx: PartialParentWithTravelerContext, _event) => {
      ctx[ParentState.travelerInformation].selectedTravelerIds = [];
      return ctx;
    }
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
